/**
 * The patient's chart name wins over a close spelling heard in the audio
 */
export function applyChartPatientName(
  text: string | null | undefined,
  chartName: string | null | undefined
): string {
  const source = text ?? '';
  const chart = normalizeChartName(chartName);
  if (!source || !chart) {
    return source;
  }
  const parts = chart.split(' ');
  if (parts.length !== 2) {
    return source;
  }
  const [first, last] = parts;
  return replaceTitledSurname(replaceGivenAndSurname(source, chart, first, last), last);
}

function normalizeChartName(chartName: string | null | undefined): string | null {
  const chart = (chartName ?? '').replace(/\s+/g, ' ').trim();
  if (!chart) {
    return null;
  }
  const key = chart.toLowerCase();
  if (
    key === 'patient' ||
    key === 'unknown' ||
    key === 'unknown patient' ||
    key === 'unknown unknown' ||
    key === 'n/a' ||
    key === 'na'
  ) {
    return null;
  }
  return chart;
}

function replaceGivenAndSurname(text: string, chart: string, first: string, last: string): string {
  const pattern = new RegExp(`\\b(${escapeRegExp(first)})\\s+([A-Za-z][A-Za-z'’-]*)\\b`, 'gi');
  return text.replace(pattern, (full, given: string, surname: string) => {
    const sameFirst = sameToken(given, first);
    const sameLast = sameToken(surname, last);
    if ((sameFirst && (sameLast || closeSpelling(surname, last))) || (closeSpelling(given, first) && sameLast)) {
      return chart;
    }
    return full;
  });
}

function replaceTitledSurname(text: string, last: string): string {
  const pattern = /\b(Mr|Mrs|Ms|Miss|Mx|Dr)\.?\s+([A-Za-z][A-Za-z'’-]*)\b/gi;
  return text.replace(pattern, (full, _title: string, surname: string) => {
    if (sameToken(surname, last) || !closeSpelling(surname, last)) {
      return full;
    }
    const splitAt = full.lastIndexOf(surname);
    return `${full.slice(0, splitAt)}${last}`;
  });
}

function sameToken(heard: string, chartToken: string): boolean {
  return heard.localeCompare(chartToken, undefined, { sensitivity: 'accent' }) === 0;
}

function closeSpelling(heard: string, chartToken: string): boolean {
  const a = heard.toLowerCase();
  const b = chartToken.toLowerCase();
  if (a === b || a.length < 3 || b.length < 3 || Math.abs(a.length - b.length) > 2) {
    return false;
  }
  return levenshtein(a, b) <= 2;
}

function levenshtein(a: string, b: string): number {
  const prev = new Array<number>(b.length + 1);
  const next = new Array<number>(b.length + 1);
  for (let j = 0; j <= b.length; j++) {
    prev[j] = j;
  }
  for (let i = 1; i <= a.length; i++) {
    next[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      next[j] = Math.min(next[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= b.length; j++) {
      prev[j] = next[j];
    }
  }
  return prev[b.length];
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
